import React, { useMemo, useState } from 'react';
import { Edit } from 'lucide-react';
import { FaTimes } from 'react-icons/fa';
import ModernTimeDropdown from '../../../dashboard-components/layouts/modernDropdown';

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const BUILT_IN_LOGIC_PARAMETERS = [
  ['conversation_type', 'Conversation type'],
  ['call_direction', 'Call direction'],
  ['attempt_number', 'Attempt number'],
  ['daily_attempt_number', 'Daily attempt number'],
  ['attempt_reason', 'Attempt reason'],
  ['disconnect_type', 'Disconnect type'],
  ['disconnect_reason', 'Disconnect reason'],
  ['transfer_status', 'Transfer status'],
  ['current_bot_id', 'Current bot ID'],
  ['parent_conversation_id', 'Parent conversation ID'],
  ['current_conversation_id', 'Current conversation ID'],
  ['customer_id', 'Customer ID'],
  ['customer_phone_number', 'Customer phone number'],
  ['request_data', 'Request data'],
  ['now', 'Current time (IST)'],
  ['max_total_attempts', 'Configured maximum total attempts'],
  ['max_reattempts_per_day', 'Configured maximum reattempts per day'],
];

const RESULT_PARAMETERS = [
  {
    key: 'should_schedule',
    label: 'should_schedule',
    description: 'Return True when a future call should be created, otherwise return False.',
    placeholder: 'if call_direction == "Inbound":\n    return False\nelse:\n    return True',
  },
  {
    key: 'scheduled_agent_id',
    label: 'scheduled_agent_id',
    description: 'Return the active same-client agent ID that should handle the scheduled call.',
    placeholder: 'if current_bot_id == "VA3FEY2G6Q":\n    return "VABMMIWVE3"\nelse:\n    return None',
  },
  {
    key: 'schedule_mode',
    label: 'schedule_mode',
    description: 'now: no timing value. delay: uses delay_minutes. callback_time: uses followup_time. Attempt limits and business hours still apply.',
    placeholder: 'if callback_agreed == "yes":\n    return "callback_time"\nelif cancer_patient_identified == "yes":\n    return "delay"\nelse:\n    return "now"',
  },
  {
    key: 'followup_time',
    label: 'followup_time',
    description: 'Used only for callback_time. Map Date and Time analytics, or return a future ISO date-time (for example 2026-12-31T10:00:00+05:30) or Unix timestamp. Times without a timezone use IST. None skips scheduling.',
    placeholder: 'if callback_agreed == "yes":\n    return followup_time\nelse:\n    return None',
  },
  {
    key: 'delay_minutes',
    label: 'delay_minutes',
    description: 'Return a non-negative delay in minutes. It is used only when schedule_mode is delay.',
    placeholder: 'if cancer_patient_identified == "yes":\n    return 1440\nelse:\n    return None',
  },
  {
    key: 'attempt_reason',
    label: 'attempt_reason',
    description: 'Return the reason that should be stored for the scheduling attempt.',
    placeholder: 'if callback_agreed == "yes":\n    return "call_back_follow_up_time_mentioned"\nelif cancer_patient_identified == "yes":\n    return "cancer_patient_identified"\nelse:\n    return "follow_up"',
  },
  {
    key: 'context_for_next_step',
    label: 'context_for_next_step',
    description: 'Optional text passed to the next bot as custom_variables.context_for_next_step.',
    placeholder: 'if callback_agreed == "yes":\n    return "Customer requested a callback"\nelse:\n    return None',
  },
];

const RESULT_PARAMETER_KEYS = RESULT_PARAMETERS.map(({ key }) => key);
const PARAMETER_EDIT_MODES = ['map', 'logic'];

const RESULT_OBJECT_KEYS = new Set([
  'should_schedule',
  'scheduled_agent_id',
  'schedule_mode',
  'followup_time',
  'callback_time',
  'schedule_parameter',
  'delay_minutes',
  'attempt_reason',
  'context_for_next_step',
]);

const SCHEDULE_MODES = ['callback_time', 'delay', 'now'];

const MAP_OUTPUT_TYPES = {
  should_schedule: ['Yes/No/NA'],
  schedule_mode: ['Categorical - Single Value'],
  followup_time: ['Date and Time'],
  delay_minutes: ['Number'],
  attempt_reason: ['Text', 'Categorical - Single Value'],
  context_for_next_step: ['Text', 'Categorical - Single Value'],
};

const RESULT_OUTPUT_TYPES = {
  should_schedule: 'Yes/No/NA',
  scheduled_agent_id: 'Text',
  schedule_mode: 'Categorical - Single Value',
  followup_time: 'Date and Time',
  delay_minutes: 'Number',
  attempt_reason: 'Text or Categorical - Single Value',
  context_for_next_step: 'Text or Categorical - Single Value',
};

const OUTSIDE_HOURS_BEHAVIORS = [
  ['schedule_in_next_business_slot', 'Schedule in next business slot'],
  ['allow_outside_business_hours', 'Allow outside business hours'],
];
const LEGACY_OUTSIDE_HOURS_BEHAVIORS = ['do_not_schedule'];

const PYTHON_KEYWORDS = new Set([
  'if', 'elif', 'else', 'and', 'or', 'not', 'in', 'is', 'return',
  'True', 'False', 'None',
]);

const FORBIDDEN_PATTERNS = [
  { pattern: /\b(import|from|for|while|def|class|try|except|finally|with|lambda|yield|raise|pass|del|global|nonlocal|async|await)\b/, message: 'Only if, elif, else, boolean expressions, comparisons, literals, and return are supported.' },
  { pattern: /\b(eval|exec|open|__import__)\b/, message: 'Function execution and filesystem access are not allowed.' },
  { pattern: /(^|[\n;])\s*[A-Za-z_]\w*\s*=(?!=)/, message: 'Assignments are not allowed; return a scheduling result directly.' },
  { pattern: /\b(if|elif)\b[^\n:]*[^=!<>]=[^=]/, message: 'Use == for comparisons. A single = is not valid Python syntax.' },
  { pattern: /\bfor\s+[A-Za-z_]\w*\s+in\b/, message: 'Loops and comprehensions are not allowed.' },
  { pattern: /\b[A-Za-z_]\w*\s+for\s+[A-Za-z_]\w*\s+in\b/, message: 'Loops and comprehensions are not allowed.' },
  { pattern: /#|;/, message: 'Comments and statement separators are not supported in this restricted editor.' },
  { pattern: /__/, message: 'Dunder names are not allowed.' },
];

const clone = (value) => {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
};

const toVariableName = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, '_')
  .replace(/[^a-z0-9_]/g, '')
  .replace(/_+/g, '_')
  .replace(/^_+|_+$/g, '');

const extractSingleReturnedString = (source) => {
  const values = Array.from(String(source || '').matchAll(/\breturn\s+("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')/g))
    .map((match) => match[1].slice(1, -1))
    .filter(Boolean);
  const uniqueValues = Array.from(new Set(values));
  return uniqueValues.length === 1 ? uniqueValues[0] : '';
};

const LOGIC_PARAMETER_KEYWORDS = new Set([
  'if', 'elif', 'else', 'and', 'or', 'not', 'in', 'is', 'return',
  'true', 'false', 'none',
]);

const extractLogicVariableNames = (source) => {
  const withoutStrings = String(source || '').replace(/("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')/g, ' ');
  return Array.from(withoutStrings.matchAll(/[A-Za-z_]\w*/g))
    .map((match) => toVariableName(match[0]))
    .filter((name) => name && !LOGIC_PARAMETER_KEYWORDS.has(name) && !name.startsWith('prev_'))
    .filter((name, index, names) => names.indexOf(name) === index);
};

const getDefaultDays = () => Object.fromEntries(
  DAYS.map(({ key }) => [key, {
    enabled: true,
    windows: [{ start: '09:00', end: '21:00' }],
  }])
);

export const createDefaultFutureSchedulingConfig = () => ({
  enabled: 'Custom',
  logic: '',
  parameter_logic: Object.fromEntries(RESULT_PARAMETER_KEYS.map((key) => [key, ''])),
  parameter_mapping: Object.fromEntries(RESULT_PARAMETER_KEYS.map((key) => [key, ''])),
  parameter_modes: Object.fromEntries(RESULT_PARAMETER_KEYS.map((key) => [key, 'logic'])),
  parameter_logic_parameters: Object.fromEntries(RESULT_PARAMETER_KEYS.map((key) => [key, []])),
  logic_parameters: [],
  business_hours: {
    days: getDefaultDays(),
    holidays: [],
    exceptions: [],
    outside_hours_behavior: 'schedule_in_next_business_slot',
  },
  attempt_policy: {
    max_total_attempts: 3,
    max_reattempts_per_day: 1,
    minimum_delay_between_attempts_minutes: 1440,
  },
});

const normalizeWindow = (window) => ({
  start: window?.start || '',
  end: window?.end || '',
});

const normalizeBusinessHours = (businessHours, defaults) => {
  const current = businessHours && typeof businessHours === 'object' ? businessHours : {};
  const currentDays = current.days && typeof current.days === 'object' ? current.days : {};
  const days = Object.fromEntries(
    DAYS.map(({ key }) => {
      const configuredDay = currentDays[key] || {};
      const defaultDay = defaults.days[key];
      return [key, {
        enabled: configuredDay.enabled === undefined ? defaultDay.enabled : configuredDay.enabled === true,
        windows: Array.isArray(configuredDay.windows)
          ? configuredDay.windows.map(normalizeWindow)
          : defaultDay.windows.map(normalizeWindow),
      }];
    })
  );

  return {
    days,
    holidays: Array.isArray(current.holidays)
      ? current.holidays.map((holiday) => ({ date: holiday?.date || '', name: holiday?.name || '' }))
      : [],
    exceptions: Array.isArray(current.exceptions)
      ? current.exceptions.map((exception) => ({
        date: exception?.date || '',
        name: exception?.name || '',
        mode: exception?.mode === 'custom' ? 'custom' : 'closed',
        windows: Array.isArray(exception?.windows) ? exception.windows.map(normalizeWindow) : [],
      }))
      : [],
    outside_hours_behavior: (
      OUTSIDE_HOURS_BEHAVIORS.some(([value]) => value === current.outside_hours_behavior)
      || LEGACY_OUTSIDE_HOURS_BEHAVIORS.includes(current.outside_hours_behavior)
    )
      ? current.outside_hours_behavior
      : defaults.outside_hours_behavior,
  };
};

const normalizeAttemptPolicy = (attemptPolicy, defaults) => {
  const current = attemptPolicy && typeof attemptPolicy === 'object' ? attemptPolicy : {};
  const configuredMinimumDelay = Number(current.minimum_delay_between_attempts_minutes);
  return {
    max_total_attempts: Number.isFinite(Number(current.max_total_attempts))
      ? Number(current.max_total_attempts)
      : defaults.max_total_attempts,
    max_reattempts_per_day: Number.isFinite(Number(current.max_reattempts_per_day))
      ? Number(current.max_reattempts_per_day)
      : defaults.max_reattempts_per_day,
    minimum_delay_between_attempts_minutes: Number.isFinite(configuredMinimumDelay)
      ? configuredMinimumDelay
      : defaults.minimum_delay_between_attempts_minutes,
  };
};

export const normalizeFutureSchedulingConfig = (value) => {
  const defaults = createDefaultFutureSchedulingConfig();
  const current = value && typeof value === 'object' ? value : {};
  const logic = typeof current.logic === 'string'
    ? current.logic
    : (typeof current.query === 'string' ? current.query : (current.python_query || ''));
  const logicParameters = Array.isArray(current.logic_parameters)
    ? current.logic_parameters
    : (Array.isArray(current.query_parameters) ? current.query_parameters : []);
  const configuredParameterLogic = current.parameter_logic && typeof current.parameter_logic === 'object'
    ? current.parameter_logic
    : (current.scheduling_parameter_logic && typeof current.scheduling_parameter_logic === 'object'
      ? current.scheduling_parameter_logic
      : {});
  const configuredParameterMapping = current.parameter_mapping && typeof current.parameter_mapping === 'object'
    ? current.parameter_mapping
    : {};
  const configuredParameterModes = current.parameter_modes && typeof current.parameter_modes === 'object'
    ? current.parameter_modes
    : {};
  const configuredParameterLogicParameters = current.parameter_logic_parameters
    && typeof current.parameter_logic_parameters === 'object'
    ? current.parameter_logic_parameters
    : {};
  const legacyFollowupTimeLogic = configuredParameterLogic.followup_time
    || configuredParameterLogic.callback_time
    || configuredParameterLogic.schedule_parameter;
  const legacyFollowupTimeMapping = configuredParameterMapping.followup_time
    || configuredParameterMapping.callback_time
    || configuredParameterMapping.schedule_parameter;
  const legacyCombinedLogic = configuredParameterLogic.schedule_parameter_or_delay_minutes;
  const legacyHasStringReturn = typeof legacyCombinedLogic === 'string' && /\breturn\s+["']/.test(legacyCombinedLogic);
  const legacyHasNumericReturn = typeof legacyCombinedLogic === 'string' && /\breturn\s+-?\d/.test(legacyCombinedLogic);
  const parameterLogic = Object.fromEntries(RESULT_PARAMETER_KEYS.map((key) => {
    let configuredValue = configuredParameterLogic[key];
    if (key === 'followup_time' && (!String(configuredValue || '').trim()) && typeof legacyFollowupTimeLogic === 'string') {
      configuredValue = legacyFollowupTimeLogic;
    }
    if (key === 'delay_minutes' && typeof configuredValue !== 'string' && legacyHasNumericReturn && !legacyHasStringReturn) {
      configuredValue = legacyCombinedLogic;
    }
    return [key, typeof configuredValue === 'string' ? configuredValue : defaults.parameter_logic[key]];
  }).filter(([, parameterValue]) => parameterValue.trim()));
  const parameterMapping = Object.fromEntries(RESULT_PARAMETER_KEYS
    .map((key) => [key, typeof configuredParameterMapping[key] === 'string' ? configuredParameterMapping[key].trim() : ''])
    .filter(([, parameterValue]) => parameterValue));
  if (!parameterMapping.followup_time && typeof legacyFollowupTimeMapping === 'string' && legacyFollowupTimeMapping.trim()) {
    parameterMapping.followup_time = legacyFollowupTimeMapping.trim();
  }
  if (!parameterMapping.followup_time) {
    const legacyCallbackTimeLogic = typeof legacyFollowupTimeLogic === 'string'
      ? legacyFollowupTimeLogic
      : (legacyHasStringReturn && !legacyHasNumericReturn ? legacyCombinedLogic : '');
    const inferredCallbackTime = extractSingleReturnedString(legacyCallbackTimeLogic);
    if (inferredCallbackTime) parameterMapping.followup_time = inferredCallbackTime;
  }
  const parameterModes = Object.fromEntries(RESULT_PARAMETER_KEYS
    .map((key) => {
      const configuredMode = key === 'followup_time'
        ? (configuredParameterModes.followup_time || configuredParameterModes.callback_time)
        : configuredParameterModes[key];
      return [key, PARAMETER_EDIT_MODES.includes(configuredMode) ? configuredMode : (parameterMapping[key] ? 'map' : 'logic')];
    })
    .filter(([key]) => parameterLogic[key] || parameterMapping[key]));
  if (parameterMapping.followup_time && !parameterLogic.followup_time) parameterModes.followup_time = 'map';

  const hasPerParameterLogicParameters = Object.keys(configuredParameterLogicParameters).length > 0;
  const parameterLogicParameters = Object.fromEntries(RESULT_PARAMETER_KEYS.map((key) => {
    const legacyConfiguredValues = key === 'followup_time'
      ? [
        configuredParameterLogicParameters.followup_time,
        configuredParameterLogicParameters.callback_time,
        configuredParameterLogicParameters.schedule_parameter,
      ]
      : [configuredParameterLogicParameters[key]];
    const configuredValue = legacyConfiguredValues.find((candidate) => Array.isArray(candidate) && candidate.length > 0)
      || legacyConfiguredValues.find((candidate) => Array.isArray(candidate));
    const configured = Array.isArray(configuredValue)
      ? configuredValue
      : [];
    const inferred = configured.length > 0
      ? configured
      : extractLogicVariableNames(parameterLogic[key] || '');
    return [key, Array.from(new Set(
      inferred
        .map((parameter) => toVariableName(parameter))
        .filter((parameter) => parameter && !parameter.startsWith('prev_'))
    ))];
  }));
  const usesLegacyCombinedLogic = logic.trim() && !Object.keys(parameterLogic).length && !Object.keys(parameterMapping).length;
  const normalizedLogicParameters = hasPerParameterLogicParameters && !usesLegacyCombinedLogic
    ? Array.from(new Set(Object.values(parameterLogicParameters).flat()))
    : Array.from(new Set(
      logicParameters
        .map((parameter) => toVariableName(parameter))
        .filter((parameter) => parameter && !parameter.startsWith('prev_'))
    ));

  return {
    enabled: current.enabled ?? 'Custom',
    parameter_defaults: { ...(current.parameter_defaults || {}) },
    runtime_variables: Array.isArray(current.runtime_variables) ? current.runtime_variables.map((item) => ({ ...item })) : [],
    logic,
    parameter_logic: parameterLogic,
    parameter_mapping: parameterMapping,
    parameter_modes: parameterModes,
    parameter_logic_parameters: parameterLogicParameters,
    logic_parameters: normalizedLogicParameters,
    business_hours: normalizeBusinessHours(current.business_hours, defaults.business_hours),
    attempt_policy: normalizeAttemptPolicy(current.attempt_policy, defaults.attempt_policy),
  };
};

const readParameterEntries = (parameters) => {
  if (Array.isArray(parameters)) {
    return parameters.map((parameter) => {
      const displayName = parameter?.display_name || parameter?.parameter_name || parameter?.name || '';
      return {
        key: toVariableName(displayName),
        label: displayName || toVariableName(displayName),
        outputType: parameter?.output_type || '',
        possibleValues: Array.isArray(parameter?.possible_values) ? parameter.possible_values : [],
        status: normalizeAnalyticsParameterStatus(parameter?.status),
      };
    });
  }
  if (parameters && typeof parameters === 'object') {
    return Object.entries(parameters).map(([key, parameter]) => {
      const displayName = parameter?.display_name || parameter?.parameter_name || parameter?.name || key;
      return {
        key: toVariableName(displayName || key),
        label: displayName || key,
        outputType: parameter?.output_type || '',
        possibleValues: Array.isArray(parameter?.possible_values) ? parameter.possible_values : [],
        status: normalizeAnalyticsParameterStatus(parameter?.status),
      };
    });
  }
  return [];
};

const normalizeAnalyticsOutputType = (type) => {
  if (type === 'Categorical') return 'Categorical - Single Value';
  if (typeof type === 'string' && type.startsWith('Date and Time')) return 'Date and Time';
  return type || '';
};

const normalizeAnalyticsParameterStatus = (status) => (
  String(status || 'active').trim().toLowerCase() === 'inactive' ? 'inactive' : 'active'
);

export const getFutureSchedulingAnalyticsFields = (analyticsConfig) => {
  // Future Scheduling can consume only calculated Inbuilt Analytics parameters.
  // Subparameters are intentionally excluded.
  const entries = readParameterEntries(analyticsConfig?.parameters);
  const fields = [];
  const seen = new Set();
  entries.forEach(({ key, label, outputType, possibleValues, status }) => {
    if (status !== 'active' || !key || key.startsWith('prev_') || seen.has(key)) return;
    seen.add(key);
    fields.push({
      value: key,
      label: `Analytics: ${label || key}`,
      outputType: normalizeAnalyticsOutputType(outputType),
      possibleValues,
      status,
    });
  });
  return fields;
};

const getLogicParameterOptions = (analyticsFields) => {
  const options = BUILT_IN_LOGIC_PARAMETERS.map(([value, label]) => ({ value, label, category: 'Built-in' }));
  const seen = new Set(options.map((option) => option.value));
  analyticsFields.forEach((field) => {
    if (!field?.value || seen.has(field.value)) return;
    seen.add(field.value);
    options.push({ value: field.value, label: field.label || field.value, category: 'Analytics' });
  });
  return options;
};

const getAnalyticsParameterOptions = (analyticsFields) => analyticsFields.filter((field) => field?.value);

const isMapCompatible = (parameterKey, analyticsField) => {
  const allowedTypes = MAP_OUTPUT_TYPES[parameterKey];
  if (!allowedTypes || !analyticsField) return false;
  if (!allowedTypes.includes(normalizeAnalyticsOutputType(analyticsField.outputType))) return false;
  if (parameterKey === 'schedule_mode' && normalizeAnalyticsOutputType(analyticsField.outputType) === 'Categorical - Single Value') {
    const possibleValues = analyticsField.possibleValues || [];
    return possibleValues.length > 0
      && possibleValues.every((value) => SCHEDULE_MODES.includes(String(value).trim().toLowerCase()));
  }
  return true;
};

const isValidDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return false;
  const [year, month, day] = String(value).split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return !Number.isNaN(date.getTime())
    && date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
};

const isValidTime = (value) => /^([01]\d|2[0-3]):[0-5]\d$/.test(String(value || ''));

const timeToMinutes = (value) => {
  if (!isValidTime(value)) return null;
  const [hours, minutes] = value.split(':').map(Number);
  return (hours * 60) + minutes;
};

const maskStrings = (source) => source.replace(/("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|"""[\s\S]*?"""|'''[\s\S]*?''')/g, (match) => match.replace(/[^\n]/g, ' '));

const getLineNumber = (source, index) => source.slice(0, index).split('\n').length;

const getIndentation = (line) => String(line || '').match(/^\s*/)?.[0].replace(/\t/g, '    ').length || 0;

const getBranchBodyStartIndex = (lines, headerIndex, endIndex) => {
  let parenthesisDepth = 0;
  for (let index = headerIndex; index < endIndex; index += 1) {
    const line = maskStrings(lines[index]);
    parenthesisDepth += (line.match(/[([{]/g) || []).length;
    parenthesisDepth -= (line.match(/[)\]}]/g) || []).length;
    if (parenthesisDepth <= 0 && /:\s*$/.test(line.trim())) return index + 1;
  }
  return headerIndex + 1;
};

const findMatchingBrace = (source, startIndex) => {
  let depth = 0;
  let quote = null;
  let escaped = false;
  for (let index = startIndex; index < source.length; index += 1) {
    const char = source[index];
    if (quote) {
      if (escaped) escaped = false;
      else if (char === '\\') escaped = true;
      else if (char === quote) quote = null;
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      continue;
    }
    if (char === '{') depth += 1;
    if (char === '}') {
      depth -= 1;
      if (depth === 0) return index;
    }
  }
  return -1;
};

const getObjectValue = (objectSource, key) => {
  const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = objectSource.match(new RegExp(`(?:["']${escapedKey}["']|${escapedKey})\\s*:\\s*("(?:\\\\.|[^"\\\\])*"|'(?:\\\\.|[^'\\\\])*'|True|False|None|-?\\d+(?:\\.\\d+)?)`, 'i'));
  return match?.[1]?.trim() || null;
};

const unquote = (value) => {
  if (!value || value.length < 2) return null;
  if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
    return value.slice(1, -1).replace(/\\(["'\\])/g, '$1');
  }
  return null;
};

const makeIssue = (message, line = 1, column = 1) => ({ message, line, column });

const validateResultObject = (objectSource, line, context) => {
  const issues = [];
  const keyMatches = Array.from(objectSource.matchAll(/(?:["']([A-Za-z_]\w*)["']|([A-Za-z_]\w*))\s*:/g));
  const keys = Array.from(new Set(keyMatches.map((match) => match[1] || match[2])));
  const allowedKeys = new Set(['should_schedule', 'scheduled_agent_id', 'schedule_mode', 'followup_time', 'callback_time', 'schedule_parameter', 'delay_minutes', 'attempt_reason', 'context_for_next_step']);
  const unknownKeys = keys.filter((key) => !allowedKeys.has(key));
  if (unknownKeys.length > 0) issues.push(makeIssue(`Unknown scheduling result field: ${unknownKeys[0]}.`, line));

  const shouldSchedule = getObjectValue(objectSource, 'should_schedule');
  if (!['True', 'False', 'true', 'false'].includes(shouldSchedule)) {
    issues.push(makeIssue('Every returned object must include should_schedule as True or False.', line));
    return issues;
  }
  const shouldScheduleValue = shouldSchedule.toLowerCase() === 'true';
  if (!shouldScheduleValue) {
    if (keys.some((key) => key !== 'should_schedule')) issues.push(makeIssue('A no-scheduling result may only contain should_schedule: False.', line));
    return issues;
  }

  ['scheduled_agent_id', 'schedule_mode', 'attempt_reason'].forEach((key) => {
    if (!getObjectValue(objectSource, key)) issues.push(makeIssue(`A scheduling result requires ${key}.`, line));
  });
  const scheduledAgentId = unquote(getObjectValue(objectSource, 'scheduled_agent_id'));
  if (!scheduledAgentId) {
    issues.push(makeIssue('scheduled_agent_id must be a selected agent ID string.', line));
  } else if (!context.agentIds.has(String(scheduledAgentId))) {
    issues.push(makeIssue(`scheduled_agent_id ${scheduledAgentId} is not an active agent for this client.`, line));
  }

  const scheduleMode = unquote(getObjectValue(objectSource, 'schedule_mode'));
  if (!SCHEDULE_MODES.includes(scheduleMode)) {
    issues.push(makeIssue('schedule_mode must be callback_time, delay, or now.', line));
    return issues;
  }
  if (scheduleMode === 'callback_time') {
    const parameter = unquote(getObjectValue(objectSource, 'followup_time') || getObjectValue(objectSource, 'callback_time') || getObjectValue(objectSource, 'schedule_parameter'));
    if (!parameter) issues.push(makeIssue('callback_time requires followup_time.', line));
    else if (!context.analyticsParameterIds.has(parameter)) issues.push(makeIssue('followup_time must be mapped to a selected analytics parameter.', line));
  }
  if (scheduleMode === 'delay') {
    const delay = getObjectValue(objectSource, 'delay_minutes');
    if (delay === null || !/^-?\d+(?:\.\d+)?$/.test(delay) || Number(delay) < 0) {
      issues.push(makeIssue('delay scheduling requires a non-negative delay_minutes value.', line));
    }
  }
  if (scheduleMode !== 'callback_time' && (keys.includes('followup_time') || keys.includes('callback_time') || keys.includes('schedule_parameter'))) issues.push(makeIssue('followup_time is only valid for callback_time scheduling.', line));
  if (scheduleMode !== 'delay' && keys.includes('delay_minutes')) issues.push(makeIssue('delay_minutes is only valid for delay scheduling.', line));
  return issues;
};

const validateLogicProgram = (logic, context) => {
  const issues = [];
  const source = String(logic || '');
  const trimmed = source.trim();
  if (!trimmed) return issues;
  const masked = maskStrings(source);

  FORBIDDEN_PATTERNS.forEach(({ pattern, message }) => {
    const match = pattern.exec(masked);
    if (match) issues.push(makeIssue(message, getLineNumber(source, match.index)));
  });

  const functionCallMatch = /\b([A-Za-z_]\w*)\s*\(/g;
  Array.from(masked.matchAll(functionCallMatch)).forEach((match) => {
    if (!PYTHON_KEYWORDS.has(match[1])) {
      issues.push(makeIssue('Function calls are not allowed in scheduling logic.', getLineNumber(source, match.index)));
    }
  });

  const lines = source.split('\n');
  const nonEmptyLines = lines.map((line, index) => ({ line, index })).filter(({ line }) => line.trim());
  const firstLine = nonEmptyLines[0];
  const firstHeader = firstLine?.line.match(/^(\s*)(if|elif|else)\b/);
  const topLevelHeaders = [];
  if (firstHeader) {
    const baseIndent = firstHeader[1].length;
    lines.forEach((line, index) => {
      const match = line.match(/^(\s*)(if|elif|else)\b/);
      if (match && match[1].length === baseIndent) topLevelHeaders.push({ index, keyword: match[2] });
    });
    if (topLevelHeaders[0]?.keyword !== 'if') issues.push(makeIssue('The logic must start with if.', firstLine.index + 1));
    if (topLevelHeaders.length > 0 && topLevelHeaders[topLevelHeaders.length - 1].keyword !== 'else') {
      issues.push(makeIssue('Every if/elif chain must end with else so every execution path returns a result.', lines.length));
    }
    topLevelHeaders.forEach((header, index) => {
      const previous = topLevelHeaders[index - 1]?.keyword;
      if (header.keyword === 'elif' && !['if', 'elif'].includes(previous)) issues.push(makeIssue('elif must follow if or another elif.', header.index + 1));
      if (header.keyword === 'else' && index !== topLevelHeaders.length - 1) issues.push(makeIssue('else must be the final branch.', header.index + 1));
    });
    topLevelHeaders.forEach((header, index) => {
      const end = topLevelHeaders[index + 1]?.index ?? lines.length;
      const bodyStart = getBranchBodyStartIndex(lines, header.index, end);
      const bodyLines = lines.slice(bodyStart, end);
      const firstBodyLine = bodyLines.find((line) => line.trim());
      if (!firstBodyLine || getIndentation(firstBodyLine) <= getIndentation(lines[header.index])) {
        issues.push(makeIssue(`${header.keyword} branch must indent its return statement.`, header.index + 1));
      }
      const body = lines.slice(header.index, end).join('\n');
      if (!/\breturn\s*\{/.test(maskStrings(body))) issues.push(makeIssue(`${header.keyword} branch must return a scheduling result object.`, header.index + 1));
    });
  } else if (!/^\s*return\s*\{/.test(masked)) {
    issues.push(makeIssue('Use an if/elif/else program or return one scheduling result object.', firstLine?.index + 1 || 1));
  }

  const allowedNames = new Set([...context.logicParameterIds, ...['True', 'False', 'None'], ...PYTHON_KEYWORDS]);
  const resultKeys = RESULT_OBJECT_KEYS;
  const identifierMatches = Array.from(masked.matchAll(/[A-Za-z_]\w*/g));
  identifierMatches.forEach((match) => {
    const name = match[0];
    const end = match.index + name.length;
    const nextNonSpace = masked.slice(end).match(/^\s*(.)/)?.[1];
    const previousNonSpace = masked.slice(0, match.index).match(/(.)\s*$/)?.[1];
    if (allowedNames.has(name) || resultKeys.has(name) || nextNonSpace === ':' || previousNonSpace === '.') return;
    issues.push(makeIssue(`Unknown variable: ${name}. Add it to the selected logic parameters.`, getLineNumber(source, match.index)));
  });

  const returnMatches = Array.from(masked.matchAll(/\breturn\b/g));
  if (returnMatches.length === 0) issues.push(makeIssue('The logic must return a scheduling result object.', 1));
  returnMatches.forEach((match) => {
    const afterReturn = source.slice(match.index + match[0].length);
    const objectOffset = afterReturn.search(/\S/);
    const objectStart = objectOffset >= 0 ? match.index + match[0].length + objectOffset : -1;
    if (objectStart < 0 || source[objectStart] !== '{') {
      issues.push(makeIssue('return must be followed by a scheduling result object.', getLineNumber(source, match.index)));
      return;
    }
    const objectEnd = findMatchingBrace(source, objectStart);
    if (objectEnd < 0) {
      issues.push(makeIssue('Scheduling result object is missing a closing brace.', getLineNumber(source, objectStart)));
      return;
    }
    issues.push(...validateResultObject(source.slice(objectStart + 1, objectEnd), getLineNumber(source, objectStart), context));
  });
  return issues;
};

const getReturnedLiteralValues = (source) => Array.from(String(source || '').matchAll(/\breturn\s+("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|True|False|None|-?\d+(?:\.\d+)?)/g))
  .map((match) => match[1])
  .map((value) => unquote(value) ?? value);

const validateParameterLogicProgram = (logic, parameterKey, context) => {
  const issues = [];
  const source = String(logic || '');
  const masked = maskStrings(source);
  const trimmed = source.trim();
  if (!trimmed) return issues;

  FORBIDDEN_PATTERNS.forEach(({ pattern, message }) => {
    const match = pattern.exec(masked);
    if (match) issues.push(makeIssue(message, getLineNumber(source, match.index)));
  });

  const functionCallMatch = /\b([A-Za-z_]\w*)\s*\(/g;
  Array.from(masked.matchAll(functionCallMatch)).forEach((match) => {
    if (!PYTHON_KEYWORDS.has(match[1])) issues.push(makeIssue('Function calls are not allowed in scheduling logic.', getLineNumber(source, match.index)));
  });

  const lines = source.split('\n');
  const nonEmptyLines = lines.map((line, index) => ({ line, index })).filter(({ line }) => line.trim());
  const firstLine = nonEmptyLines[0];
  const firstHeader = firstLine?.line.match(/^(\s*)(if|elif|else)\b/);
  if (firstHeader) {
    const baseIndent = firstHeader[1].length;
    const topLevelHeaders = [];
    lines.forEach((line, index) => {
      const match = line.match(/^(\s*)(if|elif|else)\b/);
      if (match && match[1].length === baseIndent) topLevelHeaders.push({ index, keyword: match[2] });
    });
    if (topLevelHeaders[0]?.keyword !== 'if') issues.push(makeIssue('The logic must start with if.', firstLine.index + 1));
    if (topLevelHeaders.length > 0 && topLevelHeaders[topLevelHeaders.length - 1].keyword !== 'else') {
      issues.push(makeIssue('Every if/elif chain must end with else so every path returns a value.', lines.length));
    }
    topLevelHeaders.forEach((header, index) => {
      const end = topLevelHeaders[index + 1]?.index ?? lines.length;
      const bodyStart = getBranchBodyStartIndex(lines, header.index, end);
      const bodyLines = lines.slice(bodyStart, end);
      const firstBodyLine = bodyLines.find((line) => line.trim());
      if (!firstBodyLine || getIndentation(firstBodyLine) <= getIndentation(lines[header.index])) {
        issues.push(makeIssue(`${header.keyword} branch must indent its return statement.`, header.index + 1));
      }
      const body = lines.slice(header.index, end).join('\n');
      if (!/\breturn\b/.test(maskStrings(body))) issues.push(makeIssue(`${header.keyword} branch must return a value.`, header.index + 1));
      if (header.keyword === 'else' && index !== topLevelHeaders.length - 1) issues.push(makeIssue('else must be the final branch.', header.index + 1));
    });
  } else if (!/^\s*return\b/.test(masked)) {
    issues.push(makeIssue('Use an if/elif/else program or return one value.', firstLine?.index + 1 || 1));
  }

  const allowedNames = new Set([...context.logicParameterIds, ...['True', 'False', 'None'], ...PYTHON_KEYWORDS]);
  Array.from(masked.matchAll(/[A-Za-z_]\w*/g)).forEach((match) => {
    const name = match[0];
    const end = match.index + name.length;
    const nextNonSpace = masked.slice(end).match(/^\s*(.)/)?.[1];
    if (allowedNames.has(name) || nextNonSpace === ':') return;
    issues.push(makeIssue(`Unknown variable: ${name}. Add it to the selected logic parameters.`, getLineNumber(source, match.index)));
  });

  if (Array.from(masked.matchAll(/\breturn\s*\{/g)).length > 0) {
    issues.push(makeIssue(`${parameterKey} logic must return a value, not a scheduling result object.`));
  }
  if (Array.from(masked.matchAll(/\breturn\b/g)).length === 0) issues.push(makeIssue('The logic must return a value.', 1));

  const numericInputs = new Set(['attempt_number', 'daily_attempt_number', 'now', 'max_total_attempts', 'max_reattempts_per_day']);
  const stringInputs = new Set(BUILT_IN_LOGIC_PARAMETERS.map(([key]) => key)
    .filter((key) => !numericInputs.has(key) && key !== 'request_data'));
  Array.from(masked.matchAll(/\breturn\s+([A-Za-z_]\w*)\s*(?=\n|$)/g)).forEach((match) => {
    const name = match[1];
    if (['True', 'False', 'None'].includes(name)) return;
    const field = context.analyticsFields?.find((item) => item.value === name);
    const outputType = normalizeAnalyticsOutputType(field?.outputType);
    const isNumber = numericInputs.has(name) || outputType === 'Number';
    const isString = stringInputs.has(name) || ['Text', 'Categorical - Single Value', 'Yes/No/NA', 'Date and Time'].includes(outputType);
    const isYesNo = name === 'transfer_status' || outputType === 'Yes/No/NA';
    if ((parameterKey === 'should_schedule' && !isYesNo && (isNumber || isString || name === 'request_data'))
      || (parameterKey === 'delay_minutes' && (isString || name === 'request_data'))
      || (parameterKey === 'followup_time' && name === 'request_data')
      || (['scheduled_agent_id', 'schedule_mode', 'attempt_reason', 'context_for_next_step'].includes(parameterKey) && (isNumber || name === 'request_data'))) {
      issues.push(makeIssue(`${parameterKey} cannot return ${name}: its type is incompatible.`, getLineNumber(source, match.index)));
    }
  });

  const returnedValues = getReturnedLiteralValues(source);
  if (parameterKey === 'should_schedule' && returnedValues.some((value) => !['True', 'False', 'true', 'false'].includes(value))) {
    issues.push(makeIssue('should_schedule logic must return True or False.'));
  }
  if (parameterKey === 'schedule_mode' && returnedValues.some((value) => !SCHEDULE_MODES.includes(value) && value !== 'None')) {
    issues.push(makeIssue('schedule_mode logic must return callback_time, delay, now, or None.'));
  }
  if (parameterKey === 'scheduled_agent_id') {
    const invalidAgent = returnedValues.find((value) => value !== 'None' && !context.agentIds.has(String(value)));
    if (invalidAgent) issues.push(makeIssue(`scheduled_agent_id ${invalidAgent} is not an active agent for this client.`));
  }
  if (parameterKey === 'followup_time') {
    const returns = Array.from(source.matchAll(/\breturn\s+([^\n]+)/g)).map((match) => match[1].trim());
    returns.forEach((expression) => {
      const text = unquote(expression);
      const isLiteral = text !== null && text !== undefined;
      if (!isLiteral) return; // Dynamic expressions and numeric epochs are validated by the backend.
      const field = context.analyticsFields?.find((item) => item.value === text);
      const isDateField = field && normalizeAnalyticsOutputType(field.outputType) === 'Date and Time';
      if (!isDateField && !(/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(text) && Number.isFinite(Date.parse(text)))) {
        issues.push(makeIssue('followup_time must return a timestamp, ISO date-time, Date and Time analytics parameter, or None.'));
      }
    });
  }
  if (parameterKey === 'delay_minutes') {
    const invalidDelay = returnedValues.find((value) => !['None'].includes(String(value)) && !/^(?:\d+(?:\.\d+)?)$/.test(String(value)));
    if (invalidDelay) issues.push(makeIssue('delay_minutes logic must return a non-negative number or None.'));
  }
  return issues;
};

const runtimeVariableNameValid = (name, others = []) => (
  typeof name === 'string' && /^[A-Za-z_][A-Za-z0-9_]*$/.test(name)
  && !name.startsWith('system_') && !name.startsWith('prev_')
  && !RESULT_PARAMETER_KEYS.includes(name)
  && !BUILT_IN_LOGIC_PARAMETERS.some(([key]) => key === name)
  && !others.includes(name)
);

const schedulingDefaultError = (key, value) => {
  if (key === 'should_schedule') return typeof value === 'boolean' ? '' : 'Use True or False.';
  if (key === 'schedule_mode') return ['now', 'delay', 'callback_time'].includes(value) ? '' : 'Select now, delay, or callback_time.';
  if (key === 'delay_minutes') return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? '' : 'Use a non-negative number of minutes.';
  if (key === 'followup_time') return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value) && Number.isFinite(Date.parse(value)) ? '' : 'Use an ISO date-time, including timezone when possible.';
  return typeof value === 'string' && (key === 'context_for_next_step' || value.trim()) ? '' : 'Enter a text value.';
};

const getLogicValidationIssues = (value, analyticsFields, availableBots) => {
  const config = normalizeFutureSchedulingConfig(value);
  const options = getLogicParameterOptions(analyticsFields);
  const allowedParameters = new Set(options.map((option) => option.value));
  const issues = [];
  if (config.runtime_variables.length > 4) issues.push(makeIssue('At most four additional runtime variables are allowed.'));
  Object.entries(config.parameter_defaults || {}).forEach(([key, val]) => {
    const error = schedulingDefaultError(key, val);
    if (error) issues.push(makeIssue(`${key} default: ${error}`));
  });
  const runtimeNames = [];
  config.runtime_variables.forEach((variable) => {
    if (!runtimeVariableNameValid(variable.name, runtimeNames)) issues.push(makeIssue('Runtime variable names must be unique and non-reserved.'));
    runtimeNames.push(variable.name);
    const key = 'context_for_next_step';
    issues.push(...getLogicValidationIssues({
      enabled: 'Custom', parameter_logic: { [key]: variable.logic || '' },
      parameter_modes: { [key]: variable.mode || 'logic' },
      parameter_mapping: variable.mapping ? { [key]: variable.mapping } : {},
      parameter_logic_parameters: { [key]: variable.logic_parameters || [] },
      parameter_defaults: Object.prototype.hasOwnProperty.call(variable, 'default_value') ? { [key]: variable.default_value } : {},
    }, analyticsFields, availableBots));
    if ((variable.mode || 'logic') === 'logic' && !String(variable.logic || '').trim()) issues.push(makeIssue(`${variable.name}: Logic is required.`));
    if (variable.mode === 'map' && !variable.mapping) issues.push(makeIssue(`${variable.name}: Select an analytics parameter.`));
  });
  const parameterLogic = config.parameter_logic || {};
  const parameterMapping = config.parameter_mapping || {};
  const parameterLogicParameters = config.parameter_logic_parameters || {};
  const hasParameterConfiguration = [...Object.values(parameterLogic), ...Object.values(parameterMapping)]
    .some((item) => String(item || '').trim());
  if (config.enabled === 'Yes' && (!config.logic.trim() || hasParameterConfiguration) && !String(parameterLogic.should_schedule || '').trim() && !String(parameterMapping.should_schedule || '').trim()) {
    issues.push(makeIssue('should_schedule logic is required when future scheduling is enabled.'));
  }
  const unknownParameters = Object.entries(parameterLogicParameters).flatMap(([parameterKey, parameters]) => (
    (Array.isArray(parameters) ? parameters : [])
      .filter((parameter) => !allowedParameters.has(parameter))
      .map((parameter) => `${parameterKey}: ${parameter}`)
  ));
  if (unknownParameters.length > 0) issues.push(makeIssue(`Logic parameter is not available: ${unknownParameters[0]}.`));
  const parameterLogicEntries = RESULT_PARAMETERS
    .map(({ key }) => [key, String(parameterLogic[key] || '')])
    .filter(([, logic]) => logic.trim());
  if (parameterLogicEntries.length > 0) {
    parameterLogicEntries.forEach(([parameterKey, parameterLogicValue]) => {
      issues.push(...validateParameterLogicProgram(parameterLogicValue, parameterKey, {
        logicParameterIds: new Set(
          Array.isArray(parameterLogicParameters[parameterKey])
            ? parameterLogicParameters[parameterKey]
            : config.logic_parameters
        ),
        analyticsFields,
        analyticsParameterIds: new Set(getAnalyticsParameterOptions(analyticsFields)
          .filter((field) => parameterKey !== 'followup_time' || isMapCompatible('followup_time', field))
          .map((field) => field.value)),
        agentIds: new Set((availableBots || []).map((bot) => String(bot?.id || bot?.agent_id || ''))),
      }));
    });
  } else if (config.logic.trim()) {
    issues.push(...validateLogicProgram(config.logic, {
      logicParameterIds: new Set(config.logic_parameters),
      analyticsParameterIds: new Set(getAnalyticsParameterOptions(analyticsFields).map((field) => field.value)),
      agentIds: new Set((availableBots || []).map((bot) => String(bot?.id || bot?.agent_id || ''))),
    }));
  }
  Object.entries(parameterMapping).forEach(([parameterKey, mapping]) => {
    if (!mapping) return;
    if (parameterKey === 'scheduled_agent_id') {
      const activeAgentIds = new Set((availableBots || []).map((bot) => String(bot?.id || bot?.agent_id || '')));
      if (!activeAgentIds.has(String(mapping))) {
        issues.push(makeIssue(`scheduled_agent_id ${mapping} is not an active agent for this client.`));
      }
      return;
    }
    const analyticsField = analyticsFields.find((field) => field.value === mapping);
    if (!analyticsField) {
      issues.push(makeIssue(`${parameterKey} must be mapped to an available analytics parameter.`));
      return;
    }
    if (!isMapCompatible(parameterKey, analyticsField)) {
      const expectedType = (MAP_OUTPUT_TYPES[parameterKey] || []).join(' or ');
      issues.push(makeIssue(`${parameterKey} can only map to analytics output type ${expectedType}.`));
    }
  });

  const scheduleModeMappingField = parameterMapping.schedule_mode
    ? analyticsFields.find((field) => field.value === parameterMapping.schedule_mode)
    : null;
  const possibleScheduleModes = new Set([
    ...getReturnedLiteralValues(parameterLogic.schedule_mode || ''),
    ...(scheduleModeMappingField?.possibleValues || []).map((value) => String(value).trim().toLowerCase()),
    ...(config.parameter_modes.schedule_mode === 'logic' && config.parameter_defaults.schedule_mode
      ? [config.parameter_defaults.schedule_mode] : []),
  ]);
  const modeReturns = Array.from(String(parameterLogic.schedule_mode || '').matchAll(/\breturn\s+([^\n]+)/g));
  if (modeReturns.some((match) => unquote(match[1].trim()) == null && match[1].trim() !== 'None')) {
    SCHEDULE_MODES.forEach((mode) => possibleScheduleModes.add(mode));
  }
  [['callback_time', 'followup_time'], ['delay', 'delay_minutes']].forEach(([mode, key]) => {
    if (possibleScheduleModes.has(mode) && !parameterMapping[key] && !String(parameterLogic[key] || '').trim()) {
      issues.push(makeIssue(`${key} must be configured when schedule_mode can return ${mode}.`));
    }
  });

  const businessHours = config.business_hours || {};
  if (businessHours.outside_hours_behavior !== 'allow_outside_business_hours') {
    DAYS.forEach(({ key, label }) => {
      const day = businessHours.days?.[key] || {};
      if (!day.enabled) return;
      if (!Array.isArray(day.windows) || day.windows.length === 0) {
        issues.push(makeIssue(`${label} must have at least one business-hours window.`));
        return;
      }
      day.windows.forEach((window, index) => {
        const start = timeToMinutes(window.start);
        const end = timeToMinutes(window.end);
        if (start === null || end === null || start >= end) issues.push(makeIssue(`${label} window ${index + 1} must have a valid start before end time.`));
      });
    });
  }
  (businessHours.holidays || []).forEach((holiday, index) => {
    if (!isValidDate(holiday?.date)) issues.push(makeIssue(`Holiday ${index + 1} must have a valid date.`));
  });
  (businessHours.exceptions || []).forEach((exception, index) => {
    if (!isValidDate(exception?.date)) issues.push(makeIssue(`Business-hours exception ${index + 1} must have a valid date.`));
    if (exception?.mode === 'custom') {
      if (!exception.windows?.length) issues.push(makeIssue(`Business-hours exception ${index + 1} needs a window or must be closed.`));
      exception.windows?.forEach((window) => {
        const start = timeToMinutes(window.start);
        const end = timeToMinutes(window.end);
        if (start === null || end === null || start >= end) issues.push(makeIssue(`Business-hours exception ${index + 1} has an invalid time range.`));
      });
    }
  });

  const policy = config.attempt_policy || {};
  if (!Number.isInteger(policy.max_total_attempts) || policy.max_total_attempts < 1) issues.push(makeIssue('Maximum total attempts must be at least 1.'));
  if (!Number.isInteger(policy.max_reattempts_per_day) || policy.max_reattempts_per_day < 0) issues.push(makeIssue('Maximum reattempts per day cannot be negative.'));
  if (policy.max_reattempts_per_day > policy.max_total_attempts) issues.push(makeIssue('Maximum reattempts per day cannot exceed maximum total attempts.'));
  if (!Number.isFinite(Number(policy.minimum_delay_between_attempts_minutes)) || Number(policy.minimum_delay_between_attempts_minutes) < 0) issues.push(makeIssue('Minimum delay between attempts cannot be negative.'));
  return issues;
};

export const validateFutureSchedulingConfig = (value, analyticsFields = [], availableBots = []) => (
  ['No', 'Custom'].includes(value?.enabled ?? 'Custom')
    ? []
    : getLogicValidationIssues(value, analyticsFields, availableBots).map((issue) => issue.message)
);

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
  </div>
);

const Select = ({ value, onChange, options, disabled = false, className = '', placeholder }) => (
  <ModernTimeDropdown
    value={value ?? ''}
    onChange={(event) => onChange(event.target.value)}
    options={options}
    isDisabled={disabled}
    placeholder={placeholder}
    width={`w-full ${className}`}
    height="h-10"
  />
);

const Section = ({ title, description, children }) => (
  <section className="border border-gray-200 rounded-lg p-5 bg-white">
    <h3 className="text-lg font-medium text-gray-900">{title}</h3>
    {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
    <div className="mt-4 space-y-4">{children}</div>
  </section>
);

const WindowEditor = ({ windows, onChange, addLabel = 'Add time window' }) => (
  <div className="space-y-2">
    {(windows || []).map((window, index) => (
      <div className="flex items-center gap-2" key={`${index}-${window.start}-${window.end}`}>
        <input type="time" value={window.start || ''} onChange={(event) => onChange(windows.map((item, itemIndex) => itemIndex === index ? { ...item, start: event.target.value } : item))} className="px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
        <span className="text-gray-500">to</span>
        <input type="time" value={window.end || ''} onChange={(event) => onChange(windows.map((item, itemIndex) => itemIndex === index ? { ...item, end: event.target.value } : item))} className="px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
        <button type="button" onClick={() => onChange(windows.filter((_, itemIndex) => itemIndex !== index))} className="text-red-600 hover:text-red-800 text-sm">Remove</button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...(windows || []), { start: '09:00', end: '18:00' }])} className="text-indigo-600 hover:text-indigo-800 text-sm">+ {addLabel}</button>
  </div>
);

const FutureSchedulingConfig = ({
  value,
  onChange,
  analyticsConfig,
  availableBots = [],
  showToast,
}) => {
  const config = useMemo(() => normalizeFutureSchedulingConfig(value), [value]);
  const [parameterModalOpen, setParameterModalOpen] = useState(false);
  const [parameterForm, setParameterForm] = useState({ key: '', mode: 'logic', logic: '', mapping: '' });
  const [parameterValidationIssues, setParameterValidationIssues] = useState([]);
  const analyticsFields = useMemo(() => getFutureSchedulingAnalyticsFields(analyticsConfig), [analyticsConfig]);
  const parameterOptions = useMemo(() => getLogicParameterOptions(analyticsFields), [analyticsFields]);
  const logicVariableOptions = useMemo(() => {
    const callbackTimeParameterIds = new Set(
      analyticsFields
        .filter((field) => isMapCompatible('followup_time', field))
        .map((field) => field.value)
    );
    return getLogicParameterOptions(analyticsFields)
      .filter((option) => !callbackTimeParameterIds.has(option.value));
  }, [analyticsFields]);
  const analyticsParameterOptions = useMemo(() => getAnalyticsParameterOptions(analyticsFields), [analyticsFields]);
  const parameterLabels = useMemo(() => new Map(parameterOptions.map((option) => [option.value, option.label])), [parameterOptions]);
  const validationIssues = useMemo(() => getLogicValidationIssues(config, analyticsFields, availableBots), [config, analyticsFields, availableBots]);

  const commit = (nextValue) => onChange?.(normalizeFutureSchedulingConfig(nextValue));
  const update = (path, nextValue) => {
    const next = clone(config);
    let cursor = next;
    path.slice(0, -1).forEach((key) => {
      cursor[key] = cursor[key] || {};
      cursor = cursor[key];
    });
    cursor[path[path.length - 1]] = nextValue;
    commit(next);
  };

  const getParameterLogicVariables = (parameterKey) => (
    Array.isArray(config.parameter_logic_parameters?.[parameterKey])
      ? config.parameter_logic_parameters[parameterKey]
      : []
  );

  const getSelectedLogicVariables = (parameterKey) => (
    parameterModalOpen && parameterForm.key === parameterKey
      ? parameterForm.variables || []
      : getParameterLogicVariables(parameterKey)
  );

  const getLogicVariableOptionsForParameter = (parameterKey) => (
    parameterKey === 'followup_time' ? parameterOptions : logicVariableOptions
  );

  const getUnselectedLogicVariables = (parameterKey) => {
    const selected = getSelectedLogicVariables(parameterKey);
    return getLogicVariableOptionsForParameter(parameterKey).filter((option) => !selected.includes(option.value));
  };

  const addLogicParameter = (parameterKey, parameter) => {
    const currentParameters = getSelectedLogicVariables(parameterKey);
    if (!parameter || currentParameters.includes(parameter)) return;
    setParameterForm((previous) => ({ ...previous, variables: [...currentParameters, parameter] }));
  };

  const removeLogicParameter = (parameterKey, parameter) => {
    setParameterForm((previous) => ({
      ...previous, variables: getSelectedLogicVariables(parameterKey).filter((item) => item !== parameter),
    }));
  };

  const getMapOptions = (parameterKey) => {
    if (parameterKey === 'scheduled_agent_id') {
      return availableBots.map((bot) => ({
        value: String(bot.id || bot.agent_id || ''),
        label: `${bot.id || bot.agent_id} — ${bot.name || bot.agent_name || 'Client agent'}`,
      })).filter((option) => option.value);
    }
    return analyticsFields
      .filter((field) => isMapCompatible(parameterKey, field))
      .map((field) => ({
        value: field.value,
        label: `${field.label || field.value} (${field.outputType || 'Unknown type'})`,
      }));
  };

  const openParameterModal = (parameter) => {
    const mapping = config.parameter_mapping?.[parameter.key] || '';
    const configured = mapping || config.parameter_logic?.[parameter.key];
    const mode = configured ? (config.parameter_modes?.[parameter.key] || (mapping ? 'map' : 'logic')) : 'map';
    setParameterForm({
      key: parameter.key,
      mode,
      logic: config.parameter_logic?.[parameter.key] || '',
      mapping,
      variables: [...getParameterLogicVariables(parameter.key)],
      hasDefault: Object.prototype.hasOwnProperty.call(config.parameter_defaults || {}, parameter.key),
      defaultValue: config.parameter_defaults?.[parameter.key] ?? '',
    });
    setParameterValidationIssues([]);
    setParameterModalOpen(true);
  };

  const openRuntimeVariable = (index) => {
    const variable = config.runtime_variables[index] || {};
    setParameterForm({ key: 'context_for_next_step', runtimeIndex: index,
      name: variable.name || '', mode: variable.mode || (variable.logic ? 'logic' : 'map'), logic: variable.logic || '',
      hasDefault: Object.prototype.hasOwnProperty.call(variable, 'default_value'), defaultValue: variable.default_value ?? '',
      mapping: variable.mapping || '', variables: [...(variable.logic_parameters || [])] });
    setParameterValidationIssues([]);
    setParameterModalOpen(true);
  };

  const getParameterLogicIssues = () => {
    if (!parameterForm.logic.trim()) return [];
    return validateParameterLogicProgram(parameterForm.logic, parameterForm.key, {
      logicParameterIds: new Set(parameterForm.variables || []),
      analyticsFields,
      analyticsParameterIds: new Set(analyticsParameterOptions.map((field) => field.value)),
      agentIds: new Set((availableBots || []).map((bot) => String(bot?.id || bot?.agent_id || ''))),
    });
  };

  const validateParameterLogic = () => {
    const issues = getParameterLogicIssues();
    setParameterValidationIssues(issues);
    if (issues.length > 0) {
      showToast?.(issues[0].message || 'Please fix logic validation errors.', 'error');
    } else if (parameterForm.logic.trim()) {
      showToast?.('Logic validated successfully.', 'success');
    } else {
      showToast?.('No logic configured. This parameter is optional.', 'info');
    }
    return issues.length === 0;
  };

  const saveParameterModal = () => {
    if (!parameterForm.key) return;
    if (parameterForm.mode === 'map' && !parameterForm.mapping) {
      showToast?.(`Select a compatible analytics parameter for ${parameterForm.key}.`, 'error');
      return;
    }
    if (parameterForm.runtimeIndex !== undefined && parameterForm.mode === 'logic' && !String(parameterForm.logic || '').trim()) {
      showToast?.('Logic is required for a named runtime variable.', 'error');
      return;
    }
    if (parameterForm.mode === 'logic' && !validateParameterLogic()) return;
    if (parameterForm.mode === 'logic' && parameterForm.hasDefault) {
      const error = schedulingDefaultError(parameterForm.key, parameterForm.defaultValue);
      if (error) { showToast?.(error, 'error'); return; }
    }
    if (parameterForm.runtimeIndex !== undefined) {
      const variables = [...config.runtime_variables];
      const otherNames = variables.filter((_, index) => index !== parameterForm.runtimeIndex).map((item) => item.name);
      if (!runtimeVariableNameValid(parameterForm.name, otherNames) || parameterForm.runtimeIndex >= 4) {
        showToast?.('Use a unique, non-reserved variable name. Up to four additional variables are allowed.', 'error');
        return;
      }
      variables[parameterForm.runtimeIndex] = { name: parameterForm.name, mode: parameterForm.mode,
        logic: parameterForm.mode === 'logic' ? parameterForm.logic : '',
        mapping: parameterForm.mode === 'map' ? parameterForm.mapping : '',
        logic_parameters: [...(parameterForm.variables || [])],
        ...(parameterForm.mode === 'logic' && parameterForm.hasDefault ? { default_value: parameterForm.defaultValue } : {}) };
      commit({ ...config, runtime_variables: variables });
      setParameterModalOpen(false);
      return;
    }
    const nextLogic = { ...config.parameter_logic };
    const nextMapping = { ...config.parameter_mapping };
    const nextModes = { ...config.parameter_modes, [parameterForm.key]: parameterForm.mode };
    if (parameterForm.mode === 'map') {
      nextMapping[parameterForm.key] = parameterForm.mapping;
      delete nextLogic[parameterForm.key];
    } else {
      nextLogic[parameterForm.key] = parameterForm.logic;
      delete nextMapping[parameterForm.key];
    }
    const nextDefaults = { ...config.parameter_defaults };
    delete nextDefaults[parameterForm.key];
    if (parameterForm.mode === 'logic' && parameterForm.hasDefault) nextDefaults[parameterForm.key] = parameterForm.defaultValue;
    commit({ ...config, parameter_defaults: nextDefaults, parameter_logic: nextLogic, parameter_mapping: nextMapping, parameter_modes: nextModes,
      parameter_logic_parameters: { ...config.parameter_logic_parameters, [parameterForm.key]: [...(parameterForm.variables || [])] },
    });
    setParameterModalOpen(false);
  };

  const getParameterVariablesSummary = (parameter) => {
    const mapping = config.parameter_mapping?.[parameter.key] || '';
    if (parameter.key === 'followup_time' && mapping) return parameterLabels.get(mapping) || mapping;
    const variables = getParameterLogicVariables(parameter.key)
      .map((variable) => parameterLabels.get(variable) || variable);
    return variables.length > 0 ? variables.join(', ') : '-';
  };

  return (
    <div className="">
      <Section title="Future Scheduling" description="Yes uses configured scheduling. No disables automatic callbacks. Custom uses existing client-specific scheduling.">
        <div className="flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Future scheduling
            <ModernTimeDropdown ariaLabel="Future scheduling" value={config.enabled} onChange={(event) => commit({ ...config, enabled: event.target.value })} options={['Yes', 'No', 'Custom']} width="w-full" />
          </label>
        </div>
      </Section>

      <Section title="Attempt Policy" description="The first call is always attempt 1. These limits are enforced after the scheduling decision is returned.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Maximum total attempts"><input type="number" min="1" step="1" value={config.attempt_policy.max_total_attempts} onChange={(event) => update(['attempt_policy', 'max_total_attempts'], Number(event.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></Field>
          <Field label="Maximum reattempts per day"><input type="number" min="0" step="1" value={config.attempt_policy.max_reattempts_per_day} onChange={(event) => update(['attempt_policy', 'max_reattempts_per_day'], Number(event.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></Field>
          <Field label="Minimum delay between attempts (minutes)"><input type="number" min="0" step="1" value={config.attempt_policy.minimum_delay_between_attempts_minutes} onChange={(event) => update(['attempt_policy', 'minimum_delay_between_attempts_minutes'], Number(event.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></Field>
        </div>
      </Section>

      <Section title="Scheduling Parameters" description="Map parameter is recommended when analytics already provides the value. Use logic for conditions, calculations, or fixed values.">
        <div className="overflow-x-auto mt-4">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 font-medium text-gray-700 text-left">Name</th>
                <th className="px-4 py-2 font-medium text-gray-700 text-left">Subparameters Used</th>
                <th className="px-4 py-2 font-medium text-gray-700 text-left">Logic</th>
                <th className="px-4 py-2 font-medium text-gray-700 text-left">Config Using</th>
                <th className="px-4 py-2 font-medium text-gray-700 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {RESULT_PARAMETERS.filter((parameter) => parameter.key !== 'context_for_next_step').map((parameter) => {
                const mapping = config.parameter_mapping?.[parameter.key] || '';
                const mode = config.parameter_modes?.[parameter.key] || (mapping ? 'map' : 'logic');
                const logic = config.parameter_logic?.[parameter.key] || '';
                return (
                  <tr key={parameter.key} className="align-top">
                    <td className="px-4 py-2 text-gray-900 max-w-sm">{parameter.label}<p className="mt-1 text-xs text-gray-500">{parameter.description}</p></td>
                    <td className="px-4 py-2 text-gray-700 max-w-xs whitespace-pre-wrap">{getParameterVariablesSummary(parameter)}</td>
                    <td className="px-4 py-2 text-gray-700 max-w-xs whitespace-pre-wrap">{mode === 'logic' && logic.trim() ? logic : '-'}</td>
                    <td className="px-4 py-2 text-gray-700">{mode === 'map' ? 'Map parameter' : 'Write logic'}</td>
                    <td className="px-4 py-2">
                      <button type="button" onClick={() => openParameterModal(parameter)} className="text-indigo-600 hover:text-indigo-800" aria-label={`Edit ${parameter.label}`} title={`Edit ${parameter.label}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {validationIssues.length > 0 && (
          <div className="mt-3 space-y-1" role="alert">
            {validationIssues.slice(0, 8).map((issue, index) => <p key={`${issue.message}-${index}`} className="text-sm text-red-600">{issue.message}</p>)}
          </div>
        )}
      </Section>

      <Section title="Runtime Variables for Next Step" description="Pass text values to the next bot. Calculated values replace matching custom variables; other values are preserved.">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <div><strong className="text-sm">context_for_next_step</strong><p className="text-xs text-gray-500">Optional · {config.parameter_modes.context_for_next_step === 'map' ? `Mapped to ${config.parameter_mapping.context_for_next_step || '—'}` : 'Write logic'}</p></div>
          <button type="button" onClick={() => openParameterModal(RESULT_PARAMETERS.find((item) => item.key === 'context_for_next_step'))} className="text-indigo-600 text-sm" aria-label="Edit context_for_next_step">Edit</button>
        </div>
        {config.runtime_variables.map((variable, index) => (
          <div key={index} className="flex items-center justify-between border-b border-gray-100 pb-3">
            <div><strong className="text-sm">{variable.name}</strong><p className="text-xs text-gray-500">{variable.mode === 'map' ? `Mapped to ${variable.mapping}` : 'Write logic'}</p></div>
            <div className="flex gap-4">
              <button type="button" onClick={() => openRuntimeVariable(index)} className="text-indigo-600 text-sm" aria-label={`Edit ${variable.name}`}>Edit</button>
              <button type="button" onClick={() => update(['runtime_variables'], config.runtime_variables.filter((_, itemIndex) => itemIndex !== index))} className="text-red-600 text-sm" aria-label={`Remove ${variable.name}`}>Remove</button>
            </div>
          </div>
        ))}
        <button type="button" disabled={config.runtime_variables.length >= 4} onClick={() => openRuntimeVariable(config.runtime_variables.length)} className="text-indigo-600 text-sm disabled:opacity-50">+ Add variable ({config.runtime_variables.length}/4)</button>
      </Section>

      <Section title="Scheduling behavior">
        <Field label="Outside-hours behavior">
          <Select value={config.business_hours.outside_hours_behavior} options={OUTSIDE_HOURS_BEHAVIORS} onChange={(nextValue) => update(['business_hours', 'outside_hours_behavior'], nextValue)} />
        </Field>
        {config.business_hours.outside_hours_behavior === 'allow_outside_business_hours' && (
          <p className="text-xs text-gray-500">Weekly day windows are not used while outside-hours scheduling is allowed. Holidays and exceptions remain configurable.</p>
        )}
      </Section>

      <Section title="Business Hours" description="Business hours use the fixed Asia/Kolkata (IST) timezone. No timezone is stored in the agent configuration.">
        {config.business_hours.outside_hours_behavior !== 'allow_outside_business_hours' && (
          <div className="space-y-3">
            {DAYS.map(({ key, label }) => {
              const day = config.business_hours.days[key];
              return (
                <div key={key} className="grid grid-cols-1 md:grid-cols-[150px_1fr] gap-3 items-start border-b border-gray-100 pb-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700 pt-2">
                    <input type="checkbox" checked={day.enabled} onChange={(event) => update(['business_hours', 'days', key, 'enabled'], event.target.checked)} />
                    {label}
                  </label>
                  <div className={day.enabled ? '' : 'opacity-50 pointer-events-none'}>
                    <WindowEditor windows={day.windows} onChange={(windows) => update(['business_hours', 'days', key, 'windows'], windows)} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div>
          <div className="flex items-center justify-between mb-2"><h4 className="text-sm font-medium text-gray-800">Holidays</h4><button type="button" onClick={() => update(['business_hours', 'holidays'], [...config.business_hours.holidays, { date: '', name: '' }])} className="text-indigo-600 text-sm">+ Add holiday</button></div>
          {config.business_hours.holidays.map((holiday, index) => (
            <div className="flex gap-2 mb-2" key={`holiday-${index}`}>
              <input type="date" value={holiday.date || ''} onChange={(event) => update(['business_hours', 'holidays', index, 'date'], event.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input type="text" value={holiday.name || ''} onChange={(event) => update(['business_hours', 'holidays', index, 'name'], event.target.value)} placeholder="Holiday name" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <button type="button" onClick={() => update(['business_hours', 'holidays'], config.business_hours.holidays.filter((_, itemIndex) => itemIndex !== index))} className="text-red-600 text-sm">Remove</button>
            </div>
          ))}
        </div>
        <div>
          <div className="flex items-center justify-between mb-2"><h4 className="text-sm font-medium text-gray-800">Exceptions</h4><button type="button" onClick={() => update(['business_hours', 'exceptions'], [...config.business_hours.exceptions, { date: '', name: '', mode: 'closed', windows: [] }])} className="text-indigo-600 text-sm">+ Add exception</button></div>
          {config.business_hours.exceptions.map((exception, index) => (
            <div className="border border-gray-100 rounded p-3 mb-2" key={`exception-${index}`}>
              <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_180px_auto] gap-2 items-center">
                <input type="date" value={exception.date || ''} onChange={(event) => update(['business_hours', 'exceptions', index, 'date'], event.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <input type="text" value={exception.name || ''} onChange={(event) => update(['business_hours', 'exceptions', index, 'name'], event.target.value)} placeholder="Exception name" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <Select value={exception.mode} options={[['closed', 'Closed'], ['custom', 'Custom hours']]} onChange={(nextValue) => update(['business_hours', 'exceptions', index, 'mode'], nextValue)} />
                <button type="button" onClick={() => update(['business_hours', 'exceptions'], config.business_hours.exceptions.filter((_, itemIndex) => itemIndex !== index))} className="text-red-600 text-sm">Remove</button>
              </div>
              {exception.mode === 'custom' && <div className="mt-2"><WindowEditor windows={exception.windows} onChange={(windows) => update(['business_hours', 'exceptions', index, 'windows'], windows)} addLabel="Add exception window" /></div>}
            </div>
          ))}
        </div>
      </Section>

      {parameterModalOpen && (() => {
        const selectedParameter = RESULT_PARAMETERS.find((parameter) => parameter.key === parameterForm.key);
        const mapOptions = getMapOptions(parameterForm.key);
        const selectedLogicVariables = getSelectedLogicVariables(parameterForm.key);
        const unselectedLogicVariables = getUnselectedLogicVariables(parameterForm.key);
        return (
          <div style={{ zIndex: '9999999999', position: 'fixed', top: 0, left: 0, height: '100%', width: '100%', background: 'rgba(0,0,0, 0.5)' }}>
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[calc(100vh-2rem)] flex flex-col relative">
                <div className="flex justify-between items-center p-4 pb-2">
                  <h2 className="text-lg text-gray-800" style={{ fontWeight: '550' }}>Edit Parameter</h2>
                  <button type="button" onClick={() => setParameterModalOpen(false)} className="text-gray-500 hover:text-gray-700"><FaTimes style={{ color: 'slategray' }} /></button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-6 pt-2 text-sm text-gray-600 grid gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Parameter Name</label>
                    <input
                      type="text"
                      value={parameterForm.runtimeIndex !== undefined ? parameterForm.name : (selectedParameter?.label || parameterForm.key)}
                      onChange={(event) => setParameterForm((previous) => ({ ...previous, name: event.target.value }))}
                      disabled={parameterForm.runtimeIndex === undefined}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                    />
                  </div>

                  <p className="text-xs text-gray-500">{selectedParameter?.description}</p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Output Type</label>
                    <input
                      type="text"
                      value={RESULT_OUTPUT_TYPES[parameterForm.key] || '-'}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subparameters Used</label>
                    <ModernTimeDropdown
                      value=""
                      placeholder={unselectedLogicVariables.length ? 'Select a variable' : 'All logic variables selected'}
                      options={unselectedLogicVariables.map((option) => ({ value: option.value, label: `${option.value} — ${option.label}` }))}
                      onChange={(event) => addLogicParameter(parameterForm.key, event.target.value)}
                      width="w-full"
                      height="h-10"
                      isDisabled={unselectedLogicVariables.length === 0}
                    />
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedLogicVariables.length === 0 && <span className="text-xs text-gray-500">No logic variables selected.</span>}
                      {selectedLogicVariables.map((parameter) => (
                        <span key={parameter} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                          {parameterLabels.get(parameter) || parameter}
                          <button type="button" className="text-gray-500 hover:text-gray-800" onClick={() => removeLogicParameter(parameterForm.key, parameter)} aria-label={`Remove ${parameter}`}><FaTimes style={{ fontSize: '10px' }} /></button>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Configure using</label>
                    <div className="flex gap-2">
                      {PARAMETER_EDIT_MODES.map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => {
                            setParameterForm((previous) => ({ ...previous, mode }));
                            setParameterValidationIssues([]);
                          }}
                          className={`px-4 py-2 rounded-md text-sm border ${parameterForm.mode === mode ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                        >
                          {mode === 'logic' ? 'Write logic' : 'Map parameter — Recommended'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {parameterForm.mode === 'map' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Map parameter</label>
                      <ModernTimeDropdown
                        value={parameterForm.mapping}
                        placeholder={mapOptions.length ? 'Select a parameter' : 'No compatible parameters available'}
                        options={mapOptions}
                        isDisabled={mapOptions.length === 0}
                        onChange={(event) => setParameterForm((previous) => ({ ...previous, mapping: event.target.value }))}
                        width="w-full"
                        height="h-10"
                      />
                      <p className="mt-2 text-xs text-gray-500">
                        {parameterForm.key === 'scheduled_agent_id'
                          ? 'Only active agents belonging to this client are available.'
                          : parameterForm.key === 'followup_time'
                            ? 'Select an active Date and Time analytics parameter.'
                            : 'Choose an analytics parameter with the expected output type.'}
                      </p>
                    </div>
                  ) : (
                    <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-sm font-medium text-gray-700">Python Logic</label>
                          <button type="button" onClick={validateParameterLogic} className="px-3 py-1 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-xs">Validate Logic</button>
                        </div>
                        <textarea
                          value={parameterForm.logic}
                          onChange={(event) => {
                            setParameterForm((previous) => ({ ...previous, logic: event.target.value }));
                            setParameterValidationIssues([]);
                          }}
                          rows={8}
                          spellCheck="false"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-indigo-500 font-mono text-sm"
                          placeholder={selectedParameter?.placeholder || 'Write python-style logic'}
                        />
                        <p className="mt-2 text-xs text-gray-500">Use if, elif, else, comparisons, literals, and return. Leave this empty if this output is not needed.</p>
                        <div className="mt-4 space-y-2">
                          <label className="flex items-center gap-2"><input type="checkbox" checked={!!parameterForm.hasDefault} onChange={(event) => setParameterForm((previous) => ({ ...previous, hasDefault: event.target.checked }))} />Use Default Value</label>
                          <p className="text-xs text-gray-500">Used when logic fails or returns an invalid output. Without a default, failed evaluation skips scheduling. Defaults must pass the same output validation.</p>
                          {parameterForm.hasDefault && (['should_schedule', 'schedule_mode'].includes(parameterForm.key) ? (
                            <ModernTimeDropdown placeholder="Select a default" ariaLabel="Default Value" value={String(parameterForm.defaultValue)} onChange={(event) => setParameterForm((previous) => ({ ...previous, defaultValue: parameterForm.key === 'should_schedule' ? event.target.value === 'true' : event.target.value }))} options={parameterForm.key === 'should_schedule' ? ['false', 'true'] : ['now', 'delay', 'callback_time']} width="w-full" />
                          ) : (
                            <input aria-label="Default Value" type={parameterForm.key === 'delay_minutes' ? 'number' : 'text'} min={parameterForm.key === 'delay_minutes' ? 0 : undefined} step="any" className="w-full border rounded p-2" value={parameterForm.defaultValue} onChange={(event) => setParameterForm((previous) => ({ ...previous, defaultValue: parameterForm.key === 'delay_minutes' && event.target.value !== '' ? Number(event.target.value) : event.target.value }))} />
                          ))}
                        </div>
                        {parameterValidationIssues.length > 0 && (
                          <div className="mt-2 space-y-1" role="alert">
                            {parameterValidationIssues.map((issue, index) => <p key={`${issue.message}-${index}`} className="text-sm text-red-600">{issue.message}</p>)}
                          </div>
                        )}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3 p-6">
                  <button type="button" onClick={() => setParameterModalOpen(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm">Cancel</button>
                  <button type="button" onClick={saveParameterModal} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm">Save</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default FutureSchedulingConfig;
